// ── DISCOUNT GUARDRAILS + COMPLIMENTARY ADD-ONS ─────────────────────────
//
// Pure helpers behind two Pricing Admin sections (8C, 8D). Kept free of
// React and of the pricing singletons so they unit-test on their own; the
// calculator passes DISCOUNT_GUARDRAILS / FREE_ADD_ONS in.
//
// GUARDRAILS are advisory and INTERNAL. A rep may hand-set any price; the
// calculator only labels how far below list it sits:
//   • up to the first step   → fine, nothing to say
//   • past the step, to floor → amber, needs approval
//   • past the floor          → red, plus the commission rule
// List price is always what the rate card computed for that line (the
// override row's `auto`) — never a second stored copy. Nothing here touches
// commission math or reaches a client surface.
//
// COMPLIMENTARY ADD-ONS are given at no charge, capped at a % of the quote's
// one-time contract value (the payment plan's `oneTimeScope`). They are
// printed on the proposal as "Included" lines and move NO money.

export const GUARDRAIL_OK = "ok";
export const GUARDRAIL_APPROVAL = "approval";
export const GUARDRAIL_BELOW_FLOOR = "belowFloor";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const fmtPct = (p) => {
  const n = num(p);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
};
const fmtUSD = (n) => `$${Math.round(num(n)).toLocaleString("en-US")}`;

/** % below list a sold price is. Never negative (a price ABOVE list is no
 *  discount), 0 when there is no list price to measure against. */
export function discountPctOf(listPrice, soldPrice) {
  const list = num(listPrice);
  if (list <= 0) return 0;
  const pct = ((list - num(soldPrice)) / list) * 100;
  return pct > 0 ? Math.round(pct * 100) / 100 : 0;
}

/** Which band a discount % falls in. The first step and the floor are
 *  inclusive: exactly 10% on a 10% step is still fine, exactly 20% on a 20%
 *  floor still needs approval but is not below it. */
export function classifyDiscount(pct, { firstStepPct = 0, floorPct = 0 } = {}) {
  const p = num(pct);
  const step = Math.max(0, num(firstStepPct));
  // A floor set below the step is a typo; treat the step as the floor.
  const floor = Math.max(step, num(floorPct));
  if (p <= step) return GUARDRAIL_OK;
  if (p <= floor) return GUARDRAIL_APPROVAL;
  return GUARDRAIL_BELOW_FLOOR;
}

/** The rep-facing sentence for a band, or "" when there is nothing to say. */
export function guardrailMessage(level, { firstStepPct = 0, floorPct = 0, commissionRule = "", approvalRequired = true } = {}) {
  if (level === GUARDRAIL_APPROVAL) {
    return approvalRequired
      ? `Needs approval — over the ${fmtPct(firstStepPct)}% first step`
      : `Over the ${fmtPct(firstStepPct)}% first step`;
  }
  if (level === GUARDRAIL_BELOW_FLOOR) {
    const rule = String(commissionRule || "").trim();
    return `Below the ${fmtPct(Math.max(num(firstStepPct), num(floorPct)))}% floor${rule ? ` — ${rule}` : ""}`;
  }
  return "";
}

/** The step + floor for one override line, or null when the guardrail table
 *  has no row for it (the merge seeds every PRICE_OVERRIDE_LINES id, so this
 *  only happens for a line that has no guardrail — it is then never flagged). */
export function lineGuardrailConfig(config, lineId) {
  const row = ((config && config.lines) || {})[lineId];
  if (!row) return null;
  return { firstStepPct: num(row.firstStepPct), floorPct: num(row.floorPct) };
}

function evaluate(pct, steps, config) {
  const level = classifyDiscount(pct, steps);
  return {
    pct,
    level,
    firstStepPct: steps.firstStepPct,
    floorPct: Math.max(steps.firstStepPct, steps.floorPct),
    message: guardrailMessage(level, {
      ...steps,
      commissionRule: config?.belowFloorCommissionRule,
      approvalRequired: config?.approvalRequiredBetweenStepAndFloor !== false,
    }),
  };
}

/** Guardrail verdict for one hand-set line price, or null when the line is
 *  not overridden (no override = list price = nothing to flag). */
export function lineGuardrail(config, { id, auto, override }) {
  if (override === null || override === undefined) return null;
  const steps = lineGuardrailConfig(config, id);
  if (!steps) return null;
  const out = evaluate(discountPctOf(auto, override), steps, config);
  return { id, ...out, floorPrice: Math.round(num(auto) * (1 - out.floorPct / 100)) };
}

/** Guardrail verdict for the additional proposal discount: `amount` off a
 *  `base` (the one-time scope the discount is measured against — see
 *  proposalDiscountAmount()). null when there is no discount. */
export function additionalDiscountGuardrail(config, { amount, base }) {
  if (num(amount) <= 0 || num(base) <= 0) return null;
  const steps = {
    firstStepPct: num(config?.additionalDiscountFirstStepPct),
    floorPct: num(config?.additionalDiscountFloorPct),
  };
  const pct = Math.round((num(amount) / num(base)) * 10000) / 100;
  return { id: "proposalDiscount", ...evaluate(pct, steps, config) };
}

/** Compact per-quote summary of the bands hit, or null when every price is
 *  inside its first step. Internal; rides on `inputs.guardrailFlags`. */
export function guardrailFlagsSummary(verdicts) {
  const list = (verdicts || []).filter((v) => v && v.level && v.level !== GUARDRAIL_OK);
  if (!list.length) return null;
  return {
    needsApproval: list.length > 0,
    belowFloor: list.some((v) => v.level === GUARDRAIL_BELOW_FLOOR),
    lines: list.map((v) => ({ id: v.id, level: v.level, pct: v.pct })),
  };
}

// ── Complimentary add-ons ───────────────────────────────────────────────

/** Saved / selected add-ons → [{ id, label, value, qty }]. Drops anything
 *  without an id and a label; value is whole dollars ≥ 0, qty an integer ≥ 1. */
export function normalizeFreeAddOns(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((a) => {
    if (!a || typeof a !== "object") return;
    const id = String(a.id || "").trim();
    const label = String(a.label || "").trim();
    if (!id || !label || seen.has(id)) return;
    seen.add(id);
    out.push({
      id,
      label,
      value: Math.max(0, Math.round(num(a.value))),
      qty: Math.max(1, Math.round(num(a.qty) || 1)),
    });
  });
  return out;
}

/** Total value of the complimentary add-ons (value × qty). */
export function freeAddOnsValue(list) {
  return normalizeFreeAddOns(list).reduce((s, a) => s + a.value * a.qty, 0);
}

/** $ cap = capPct % of the one-time contract value. */
export function freeAddOnCap(oneTimeScope, capPct) {
  return Math.max(0, Math.round(num(oneTimeScope) * (Math.max(0, num(capPct)) / 100)));
}

/** Where a selection stands against the cap. */
export function freeAddOnsStatus(list, { oneTimeScope = 0, capPct = 0 } = {}) {
  const total = freeAddOnsValue(list);
  const cap = freeAddOnCap(oneTimeScope, capPct);
  return { total, cap, remaining: cap - total, over: total > cap };
}

/** The message shown when a selection would take the quote past the cap. */
export function freeAddOnCapMessage(capPct, cap) {
  return `Over the ${fmtPct(capPct)}% cap (${fmtUSD(cap)}) — needs admin approval`;
}

/** Can `next` (the whole proposed selection) be saved? Shrinking a selection
 *  is always allowed, so a rep is never stuck on a quote whose contract value
 *  later fell below what was already given. */
export function freeAddOnsChangeAllowed(prev, next, { oneTimeScope = 0, capPct = 0 } = {}) {
  const after = freeAddOnsStatus(next, { oneTimeScope, capPct });
  if (!after.over) return true;
  return freeAddOnsValue(next) <= freeAddOnsValue(prev);
}

/** Client-facing scope-line copy — mirrored by `_free_add_on_lines()` in
 *  backend/routers/vexos_proposal_pdf.py. */
export function freeAddOnLineCopy({ label, value, qty }) {
  const q = Math.max(1, Math.round(num(qty) || 1));
  const total = Math.max(0, Math.round(num(value))) * q;
  return {
    label: `${q > 1 ? `${q} × ` : ""}${label} — complimentary`,
    value: "Included",
    sub: `Value ${fmtUSD(total)}, included at no charge`,
  };
}
